import React from "react";
import { components } from "react-select";
import Avatar from "components/common/Avatar";
import type { SelectOption } from "types/api.types";

/** Minimal user shape for default option / display (e.g. nested owner on a record). */
export type UserSelectSnapshot = {
  id?: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  avatar?: string | null;
} | null;

/**
 * Option row from `GET /users/options` or `GET /users/options/with-employee-profile`.
 * API may send extra fields — renderers read defensively.
 */
export type UserSelectOptionData = SelectOption & {
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
  email?: string | null;
  userEmail?: string | null;
};

function pickString(...vals: Array<unknown>): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function getUserDisplayName(opt: UserSelectOptionData): string {
  const joined = [opt.firstName, opt.lastName].filter(Boolean).join(" ").trim();
  return pickString(joined, opt.label) || `User #${opt.value}`;
}

function getUserEmailLine(opt: UserSelectOptionData): string {
  const e = pickString(opt.email, opt.userEmail);
  return e || "—";
}

/** Dropdown row: avatar, display name, email. */
export function UserSelectOption(props: any) {
  const { data, innerRef, innerProps, isDisabled } = props;
  const opt = data as UserSelectOptionData;
  const name = getUserDisplayName(opt);
  const emailLine = getUserEmailLine(opt);

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="d-flex align-items-center gap-2 px-2 py-2"
      style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
    >
      <div className="flex-shrink-0 avatar-sm">
        <Avatar
          name={name}
          image={opt.avatar ?? undefined}
          className="rounded-circle w-100 h-100"
        />
      </div>
      <div className="flex-grow-1 min-w-0">
        <span className="fw-medium text-body text-truncate d-block">{name}</span>
        <span className="text-muted small text-truncate d-block">{emailLine}</span>
      </div>
    </div>
  );
}

/** Selected value chip: avatar, name, email. */
export function UserSelectSingleValue(props: any) {
  const { data } = props;
  const opt = data as UserSelectOptionData;
  const name = getUserDisplayName(opt);
  const emailLine = getUserEmailLine(opt);

  return (
    <components.SingleValue {...props}>
      <div className="d-flex align-items-center gap-2 w-100 overflow-hidden">
        <div className="flex-shrink-0 avatar-sm">
          <Avatar
            name={name}
            image={opt.avatar ?? undefined}
            className="rounded-circle w-100 h-100"
          />
        </div>
        <div className="min-w-0 text-truncate d-flex flex-column">
          <span className="fw-medium text-truncate">{name}</span>
          <span className="text-muted small text-truncate">{emailLine}</span>
        </div>
      </div>
    </components.SingleValue>
  );
}

/** Pass to `AsyncPaginatedSelect` with `fetchOptions={getUserOptionsWithEmployeeProfile}` (or plain user options). */
export const userSelectWithAvatarComponents = {
  Option: UserSelectOption,
  SingleValue: UserSelectSingleValue,
} as const;

export function buildUserSelectDefaultOption(params: {
  ownerId?: number | null;
  owner?: UserSelectSnapshot;
  /** Shown when name/email are missing (e.g. change-owner modal). */
  resourceCode?: string | null;
  /** Explicit label override when you need custom copy (e.g. "You (default)"). */
  labelOverride?: string | null;
}): UserSelectOptionData | undefined {
  const ownerId =
    params.ownerId != null && params.ownerId > 0
      ? params.ownerId
      : params.owner?.id != null && params.owner.id > 0
        ? params.owner.id
        : undefined;
  if (ownerId == null || ownerId <= 0) return undefined;

  const po = params.owner;
  const fn = po?.firstName ?? "";
  const ln = po?.lastName ?? "";
  const name = [fn, ln].filter(Boolean).join(" ").trim();
  const rc = params.resourceCode?.trim();
  const labelOverride = params.labelOverride?.trim();

  const label =
    labelOverride ||
    name ||
    po?.email?.trim() ||
    (rc ? `${rc} · owner` : "") ||
    `User #${ownerId}`;

  return {
    value: ownerId,
    label: label || `User #${ownerId}`,
    firstName: fn || undefined,
    lastName: ln || undefined,
    avatar: po?.avatar ?? undefined,
    email: po?.email ?? undefined,
  };
}
